import { useState, type FormEvent } from "react";
import { Search } from "lucide-react";
import { Icon } from "../ui";
import { cx } from "../ui/cx";

export interface SearchBoxProps {
  initial?: string;
  onSearch: (query: string) => void;
  size?: "md" | "lg";
  autoFocus?: boolean;
  className?: string;
}

/** Where a search goes: the words, and the department if one was chosen (KALI-31). */
export const searchPath = (query: string, productTypeId?: number) =>
  `/search?q=${encodeURIComponent(query)}${productTypeId ? `&type=${productTypeId}` : ""}`;

/** One box for part numbers and words. Searches on Enter, never on every keystroke. */
export function SearchBox({ initial = "", onSearch, size = "md", autoFocus, className }: SearchBoxProps) {
  const [query, setQuery] = useState(initial);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim()) onSearch(query.trim());
  };
  return (
    <form role="search" onSubmit={submit} className={cx("flex", className)}>
      <label className="sr-only" htmlFor={`search-${size}`}>
        Search by part number or name
      </label>
      <span
        className={cx(
          "flex min-w-0 flex-1 items-center gap-2 rounded-l-sm border border-r-0 border-border-default bg-surface-card px-3 inset-shadow-default focus-within:border-green-700",
          size === "lg" ? "h-(--control-lg)" : "h-(--control-md)",
        )}
      >
        <Icon icon={Search} size={18} className="text-ink-400" />
        <input
          id={`search-${size}`}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Part number or name, e.g. KM170F-01018"
          autoFocus={autoFocus}
          autoComplete="off"
          enterKeyHint="search"
          className="min-w-0 flex-1 border-none bg-transparent type-body [font-size:var(--text-base)] text-text-strong outline-none"
        />
      </span>
      <button
        type="submit"
        className={cx(
          "flex-none cursor-pointer rounded-r-sm bg-green-700 px-4 type-label text-white hover:bg-green-800",
          size === "lg" ? "h-(--control-lg)" : "h-(--control-md)",
        )}
      >
        Search
      </button>
    </form>
  );
}
